/**
 * Optional local theme pack.
 *
 * Lets an operator point the game at their own card art without that art ever
 * entering the repository or the container image. The server reads a theme
 * from a gitignored asset directory at runtime; when nothing is there, the
 * built-in original artwork is used.
 */
import { COLOR_SUITS } from './cards.js';
export const EMPTY_THEME = {
    name: '',
    cardImages: {},
    suitColors: {},
    background: null
};
// Colours run 1-9 but rockets only go up to 4.
const CARD_ID = /^(?:(?:pink|green|blue|yellow)[1-9]|rocket[1-4])$/;
const COLOUR = /^#[0-9a-fA-F]{3,8}$/;
/** Only same-origin asset paths are accepted, so a theme cannot point at a third party. */
function safeAssetPath(value) {
    if (typeof value !== 'string')
        return null;
    const trimmed = value.trim();
    if (!trimmed.startsWith('/assets/'))
        return null;
    if (trimmed.includes('..') || trimmed.includes('//'))
        return null;
    if (trimmed.length > 300)
        return null;
    return trimmed;
}
/**
 * Coerces untrusted JSON into a Theme, dropping anything malformed. Never
 * throws - a broken pack should degrade to the built-in art, not break the game.
 */
export function parseTheme(input) {
    if (!input || typeof input !== 'object')
        return EMPTY_THEME;
    const raw = input;
    const cardImages = {};
    if (raw.cardImages && typeof raw.cardImages === 'object') {
        for (const [key, value] of Object.entries(raw.cardImages)) {
            if (!CARD_ID.test(key))
                continue;
            const path = safeAssetPath(value);
            if (path)
                cardImages[key] = path;
        }
    }
    const suitColors = {};
    if (raw.suitColors && typeof raw.suitColors === 'object') {
        const allowed = [...COLOR_SUITS, 'rocket'];
        for (const [key, value] of Object.entries(raw.suitColors)) {
            if (!allowed.includes(key))
                continue;
            if (typeof value === 'string' && COLOUR.test(value.trim())) {
                suitColors[key] = value.trim();
            }
        }
    }
    return {
        name: typeof raw.name === 'string' ? raw.name.slice(0, 60) : '',
        cardImages,
        suitColors,
        background: safeAssetPath(raw.background)
    };
}
export function isThemeEmpty(theme) {
    return (Object.keys(theme.cardImages).length === 0 &&
        Object.keys(theme.suitColors).length === 0 &&
        theme.background === null);
}
//# sourceMappingURL=theme.js.map